import { useState, type FC, type HTMLAttributeAnchorTarget, type ReactElement } from 'react';
import { graphql, useFragment } from 'react-relay';
import { ProductImageCarousel } from './Carousel/ProductImageCarousel.tsx';
import {
    type ImageBackgroundColor,
    type OnClick,
    type SrcSet,
    type TileSize,
} from './constants.ts';

import styles from './styles/styles.scss';

import { type BuyerProductCarouselOnly_viewer$key } from './__generated__/BuyerProductCarouselOnly_viewer.graphql.ts';
import { type BuyerProductCarouselOnly_item$key } from './__generated__/BuyerProductCarouselOnly_item.graphql.ts';

const viewerFragment = graphql`
    fragment BuyerProductCarouselOnly_viewer on Viewer {
        ...ProductImageCarousel_viewer
    }
`;

const itemFragment = graphql`
    fragment BuyerProductCarouselOnly_item on Item {
        ...ProductImageCarousel_item
    }
`;

type Props = {
    viewer: BuyerProductCarouselOnly_viewer$key | null | undefined;
    item: BuyerProductCarouselOnly_item$key | null | undefined;
    buyerHost?: string;
    imageBackgroundColor?: ImageBackgroundColor;
    disableLinks?: boolean;
    imageLoadVerticalOffset?: number;
    index: number;
    itemsPerRow: number;
    linkTarget?: HTMLAttributeAnchorTarget;
    onClick?: OnClick;
    onContentLoaded?: () => void;
    size: TileSize;
    srcSet?: SrcSet;
    showSwipeIndicator?: boolean;
    favoritesComponent?: ReactElement;
};

export const BuyerProductCarouselOnly: FC<Props> = ({
    viewer: viewerRef,
    item: itemRef,
    buyerHost,
    imageBackgroundColor,
    disableLinks,
    imageLoadVerticalOffset,
    index,
    itemsPerRow,
    linkTarget,
    onClick,
    onContentLoaded,
    size,
    srcSet,
    showSwipeIndicator,
    favoritesComponent,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const [isHovering, setIsHovering] = useState(false);
    const [wasHovered, setWasHovered] = useState(false);

    return (
        <article
            className={styles.itemContainer}
            onMouseEnter={() => {
                setIsHovering(true);
                if (!wasHovered) {
                    setWasHovered(true);
                }
            }}
            onMouseLeave={() => setIsHovering(false)}
        >
            <ProductImageCarousel
                viewer={viewer}
                item={item}
                buyerHost={buyerHost}
                imageBackgroundColor={imageBackgroundColor}
                disableLinks={disableLinks}
                imageLoadVerticalOffset={imageLoadVerticalOffset}
                index={index}
                itemsPerRow={itemsPerRow}
                isHovering={isHovering}
                linkTarget={linkTarget}
                onClick={onClick}
                onContentLoaded={onContentLoaded}
                size={size}
                srcSet={srcSet}
                wasHovered={wasHovered}
                showSwipeIndicator={showSwipeIndicator}
            />
            {favoritesComponent}
        </article>
    );
};
